import React, { useState, useRef, useEffect } from 'react';
import { X, Image as ImageIcon, MapPin, Save, User as UserIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageCropperModal } from './ImageCropperModal';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import toast from 'react-hot-toast';

// Fix for default marker icon in react-leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface User {
  id: string;
  name: string;
  role: string;
}

interface ClubProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (clubData: any) => Promise<void>;
  initialData?: any;
  users: User[];
}

function LocationPicker({ position, setPosition }: { position: [number, number] | null, setPosition: (p: [number, number]) => void }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
}

const AMENITIES_LIST = [
  { id: 'vestiario', label: 'Vestiário' },
  { id: 'lanchonete', label: 'Lanchonete' },
  { id: 'estacionamento', label: 'Estacionamento' },
  { id: 'loja', label: 'Loja de Artigos' },
  { id: 'churrasqueira', label: 'Churrasqueira' },
  { id: 'iluminacao', label: 'Iluminação' },
];

export const ClubProfileModal: React.FC<ClubProfileModalProps> = ({ isOpen, onClose, onSave, initialData, users }) => {
  const [formData, setFormData] = useState<any>({
    name: '',
    slogan: '',
    managerId: '',
    address: '',
    city: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    amenities: [],
    coverUrl: '',
    logoUrl: ''
  });
  const [position, setPosition] = useState<[number, number] | null>(null);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropType, setCropType] = useState<'cover' | 'logo' | null>(null);
  const [tempImage, setTempImage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        id: initialData?.id,
        name: initialData?.name || '',
        slogan: initialData?.slogan || '',
        managerId: initialData?.managerId || '',
        address: initialData?.address || '',
        city: initialData?.city || '',
        phone: initialData?.phone || '',
        whatsapp: initialData?.whatsapp || '',
        email: initialData?.email || '',
        website: initialData?.website || '',
        amenities: initialData?.amenities || [],
        coverUrl: initialData?.coverUrl || '',
        logoUrl: initialData?.logoUrl || ''
      });
      if (initialData?.latitude && initialData?.longitude) {
        setPosition([initialData.latitude, initialData.longitude]);
      } else {
        setPosition(null);
      }
    }
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleAmenityToggle = (amenityId: string) => {
    setFormData((prev: any) => {
      const current = prev.amenities || [];
      if (current.includes(amenityId)) {
        return { ...prev, amenities: current.filter((id: string) => id !== amenityId) };
      } else {
        return { ...prev, amenities: [...current, amenityId] };
      }
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'logo') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempImage(reader.result as string);
        setCropType(type);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBase64: string) => {
    if (cropType === 'cover') setFormData((prev: any) => ({ ...prev, coverUrl: croppedBase64 }));
    if (cropType === 'logo') setFormData((prev: any) => ({ ...prev, logoUrl: croppedBase64 }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return toast.error('O nome do clube é obrigatório');
    
    setIsProcessing(true);
    try {
      await onSave({
        ...formData,
        latitude: position ? position[0] : null,
        longitude: position ? position[1] : null
      });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar clube');
    } finally {
      setIsProcessing(false);
    }
  };

  const eligibleManagers = users.filter(u => u.role === 'GESTOR' || u.role === 'ADMIN' || u.role === 'GESTOR_COMPETICAO' || u.role === 'GESTOR_DE_COMPETICAO');

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            style={{
              width: '100%', maxWidth: '600px', maxHeight: '90vh',
              backgroundColor: "#1A112C", borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden',
              display: 'flex', flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>
                {initialData?.id ? 'Editar Clube' : 'Novo Clube'}
              </h2>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Capa */}
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Foto de Capa</label>
                <div 
                  onClick={() => { setCropType('cover'); fileInputRef.current?.click(); }}
                  style={{
                    width: '100%', height: '200px', borderRadius: '12px',
                    background: formData.coverUrl ? `url(${formData.coverUrl}) center/cover` : 'rgba(255,255,255,0.05)',
                    border: '1px dashed rgba(255,255,255,0.2)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'var(--primary-color)'
                  }}
                >
                  {!formData.coverUrl && (
                    <>
                      <ImageIcon size={32} style={{ marginBottom: '8px' }} />
                      <span>Adicionar foto de capa</span>
                    </>
                  )}
                </div>
              </div>

              {/* Informações Básicas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Logo (Opcional)</label>
                    <div 
                      onClick={() => { setCropType('logo'); fileInputRef.current?.click(); }}
                      style={{
                        width: '80px', height: '80px', borderRadius: '12px',
                        background: formData.logoUrl ? `url(${formData.logoUrl}) center/cover` : 'rgba(255,255,255,0.05)',
                        border: '1px dashed rgba(255,255,255,0.2)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: 'var(--primary-color)'
                      }}
                    >
                      {!formData.logoUrl && <ImageIcon size={24} />}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Nome do Clube *</label>
                    <input
                      name="name" value={formData.name} onChange={handleChange}
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Slogan (opcional)</label>
                  <input
                    name="slogan" value={formData.slogan} onChange={handleChange}
                    style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Gestor Responsável</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <UserIcon size={20} color="rgba(255,255,255,0.5)" />
                    <select
                      name="managerId" value={formData.managerId} onChange={handleChange}
                      style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    >
                      <option value="">Selecione um gestor...</option>
                      {eligibleManagers.map(m => (
                        <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Localização */}
              <div>
                <h3 style={{ color: 'white', margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={20} color="var(--primary-color)" /> Localização
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ height: '200px', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <MapContainer center={position || [-23.5505, -46.6333]} zoom={13} style={{ height: '100%', width: '100%', zIndex: 1 }} scrollWheelZoom={true}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <LocationPicker position={position} setPosition={setPosition} />
                    </MapContainer>
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Clique no mapa para definir o pino de localização.</div>
                  
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px' }}>Endereço Completo</label>
                    <input
                      name="address" value={formData.address} onChange={handleChange} placeholder="Ex: Rua das Flores, 123"
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '14px' }}>Cidade</label>
                    <input
                      name="city" value={formData.city} onChange={handleChange}
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div>
                <h3 style={{ color: 'white', margin: '0 0 16px 0', fontSize: '16px' }}>Informações de Contato</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '12px' }}>Telefone</label>
                    <input
                      name="phone" value={formData.phone} onChange={handleChange}
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '12px' }}>WhatsApp</label>
                    <input
                      name="whatsapp" value={formData.whatsapp} onChange={handleChange}
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '12px' }}>E-mail</label>
                    <input
                      name="email" value={formData.email} onChange={handleChange}
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontSize: '12px' }}>Website URL</label>
                    <input
                      name="website" value={formData.website} onChange={handleChange} placeholder="https://"
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                    />
                  </div>
                </div>
              </div>

              {/* Comodidades */}
              <div>
                <h3 style={{ color: 'white', margin: '0 0 16px 0', fontSize: '16px' }}>Comodidades</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {AMENITIES_LIST.map(amenity => {
                    const isSelected = formData.amenities?.includes(amenity.id);
                    return (
                      <div 
                        key={amenity.id}
                        onClick={() => handleAmenityToggle(amenity.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: `1px solid ${isSelected ? 'var(--primary-color)' : 'rgba(255,255,255,0.2)'}`,
                          background: isSelected ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
                          color: isSelected ? 'var(--primary-color)' : 'white',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {amenity.label}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div style={{
              padding: '20px', borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', justifyContent: 'flex-end', gap: '16px',
              background: 'rgba(0,0,0,0.2)'
            }}>
              <button
                onClick={onClose}
                style={{ padding: '12px 24px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isProcessing}
                style={{ padding: '12px 24px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Save size={20} />
                {isProcessing ? 'Salvando...' : 'Salvar Clube'}
              </button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={(e) => handleFileSelect(e, cropType as any)} />
      <ImageCropperModal
        isOpen={cropperOpen}
        imageSrc={tempImage}
        onClose={() => setCropperOpen(false)}
        onCropComplete={handleCropComplete}
        aspect={cropType === 'cover' ? 3 : 1}
      />
    </>
  );
};
